import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in Leaflet with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet's default icon path issue
delete (L.Icon.Default.prototype as { _getIconUrl?: () => string })._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

type LatLng = {
    lat: number;
    lng: number;
};

type Props = {
    width?: string | number;
    height?: string | number;
    latLng?: LatLng;
    searchEnabled?: boolean;
    searchPosition?: 'top' | 'bottom';
    onChange?: (latLng: LatLng) => void;
};

type DraggableMarkerProps = {
    position: LatLng;
    onDragEnd: (latLng: LatLng) => void;
};

function DraggableMarker({ position, onDragEnd }: DraggableMarkerProps) {
    const markerRef = useRef<L.Marker>(null);

    const eventHandlers = {
        dragend() {
            const marker = markerRef.current;
            if (marker) {
                const newPos = marker.getLatLng();
                onDragEnd({ lat: newPos.lat, lng: newPos.lng });
            }
        },
    };

    return (
        <Marker
            draggable
            eventHandlers={eventHandlers}
            position={[position.lat, position.lng]}
            ref={markerRef}
        />
    );
}

type MapClickHandlerProps = {
    onClick: (latLng: LatLng) => void;
};

function MapClickHandler({ onClick }: MapClickHandlerProps) {
    useMapEvents({
        click(e) {
            onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
}

/**
 * OpenStreetMap component with draggable marker and optional search
 */
export function SttOpenStreetMap({
    width = '100%',
    height = 400,
    latLng = { lat: 40.3315573, lng: -3.5786729 },
    searchEnabled = false,
    searchPosition = 'top',
    onChange,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [markerPosition, setMarkerPosition] = useState<LatLng>(latLng);
    const [mapCenter, setMapCenter] = useState<LatLng>(latLng);
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const mapRef = useRef<L.Map>(null);

    useEffect(() => {
        setMarkerPosition(latLng);
        setMapCenter(latLng);
    }, [latLng.lat, latLng.lng]);

    const handlePositionChange = (newPosition: LatLng) => {
        setMarkerPosition(newPosition);
        onChange?.(newPosition);
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setSearching(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`,
            );
            const data = await response.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                const newPosition = {
                    lat: parseFloat(lat),
                    lng: parseFloat(lon),
                };
                setMarkerPosition(newPosition);
                setMapCenter(newPosition);
                onChange?.(newPosition);

                // Pan map to new location
                if (mapRef.current) {
                    mapRef.current.setView(
                        [newPosition.lat, newPosition.lng],
                        15,
                    );
                }
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setSearching(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const searchBox = searchEnabled && (
        <Box className="mb-2">
            <TextField
                fullWidth
                size="small"
                placeholder={t('page.sc.conf.data.searchMap')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={searching}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleSearch}
                                disabled={searching}
                                size="small"
                            >
                                <SearchIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );

    return (
        <Box sx={{ width, height: 'auto' }}>
            {searchPosition === 'top' && searchBox}

            <MapContainer
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={15}
                style={{
                    height: typeof height === 'number' ? `${height}px` : height,
                    width: '100%',
                }}
                ref={mapRef}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <DraggableMarker
                    position={markerPosition}
                    onDragEnd={handlePositionChange}
                />
                <MapClickHandler onClick={handlePositionChange} />
            </MapContainer>

            {searchPosition === 'bottom' && searchBox}
        </Box>
    );
}
